import { useCompany } from '@dispatch/shared/hooks';
import {
  AssignmentCategoryOptions,
  AssignmentTypeOptions,
  AttributeOptions,
  FNOL_TaskTypeOptions,
  FNOL_TaskTypes,
  IngressContactMethodOptions,
  IngressRequestForm,
  InspectionTypeOptions,
  PriorityLevelOptions,
  PropertyTypeOptions,
  SeverityOptions,
} from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateField,
  DateTimeField,
  Section,
  SelectField,
  SubSection,
  TextField,
} from '@dispatch/web/ui';
import { Divider, Grid } from '@mui/material';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form';
import { IngressClaimAttributesSection } from './IngressClaimAttributes/IngressClaimAttributesSection';
import { useMemo } from 'react';
import { useGeneralDataContext } from '@dispatch/shared/hooks';

export type IngressSectionProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  watch: UseFormWatch<IngressRequestForm>;
  getValues: UseFormGetValues<IngressRequestForm>;
  companyAttributes: AttributeOptions[];
};

export const IngressRequestSection = ({
  control,
  errors,
  watch,
  getValues,
  companyAttributes,
}: IngressSectionProps) => {
  const { statesOptions, methodOfInspectionOptions } = useGeneralDataContext();
  const { perilOptions, featureFlags } = useCompany();

  const isNonComplex = useMemo(
    () =>
      getValues('assignmentHandling.taskType') === FNOL_TaskTypes.NON_COMPLEX,
    [getValues]
  );

  const preferredLanguages = useMemo(
    () =>
      companyAttributes
        .find((attribute) => attribute.title === 'Languages Spoken')
        ?.attributeDefinitionValues.map((value) => ({
          title: value.title,
          value: value.value,
        })),
    [companyAttributes]
  );

  const renderClaimAttributes = () => {
    return (
      <IngressClaimAttributesSection
        control={control}
        watch={watch}
        getValues={getValues}
        companyAttributes={companyAttributes}
      />
    );
  };

  const renderDynamicFields = (isNonComplex: boolean) => {
    if (isNonComplex) {
      return (
        <>
          <Grid item xs={6}>
            <DateTimeField
              id="mustStartAfter"
              name="assignmentHandling.mustStartAfter"
              label="Must Start After"
              control={control}
              errors={errors['assignmentHandling']}
              required={isNonComplex}
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimeField
              id="mustEndBefore"
              name="assignmentHandling.mustEndBefore"
              label="Must End Before"
              control={control}
              errors={errors['assignmentHandling']}
              required={isNonComplex}
            />
          </Grid>
        </>
      );
    }

    return (
      <>
        <Grid item xs={6}>
          <DateField
            id="mustStartAfter"
            name="assignmentHandling.mustStartAfter"
            label="Must Start After"
            control={control}
            errors={errors['assignmentHandling']}
            required={isNonComplex}
          />
        </Grid>
        <Grid item xs={6}>
          <DateField
            id="mustEndBefore"
            name="assignmentHandling.mustEndBefore"
            label="Must End Before"
            control={control}
            errors={errors['assignmentHandling']}
            required={isNonComplex}
          />
        </Grid>
      </>
    );
  };

  return (
    <Section title={'Ingress Request'}>
      <SubSection title="Required Resource">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="requiredResource"
              name="requiredResource.title"
              label="Required Resource"
              control={control}
              errors={errors['claim']?.['requiredResource']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <SubSection title="Loss Location">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="streetLine1"
              name="claim.lossLocation.streetLine1"
              label="Street Line 1"
              control={control}
              errors={errors['claim']?.['lossLocation']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="streetLine2"
              name="claim.lossLocation.streetLine2"
              label="Street Line 2"
              control={control}
              errors={errors['claim']?.['lossLocation']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="city"
              name="claim.lossLocation.city"
              label="City"
              control={control}
              errors={errors['claim']?.['lossLocation']}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="state"
              name="claim.lossLocation.state"
              label="State"
              control={control}
              errors={errors['claim']?.['lossLocation']}
              options={statesOptions}
              required
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="zip"
              name="claim.lossLocation.zip"
              label="Zip"
              control={control}
              errors={errors['claim']?.['lossLocation']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <CheckBoxField
            id="contact.hasAlternateLocation"
            name="contact.hasAlternateLocation"
            label="Use Alternate Location"
            control={control}
          />
        </Grid>
      </Grid>
      {watch('contact.hasAlternateLocation') && (
        <>
          <Divider style={{ marginTop: 20, marginBottom: 20 }} />
          <SubSection title="Alternate Loss Location">
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="streetLine1"
                  name="contact.altLocation.streetLine1"
                  label="Street Line 1"
                  control={control}
                  errors={errors['contact']?.['altLocation']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="streetLine2"
                  name="contact.altLocation.streetLine2"
                  label="Street Line 2"
                  control={control}
                  errors={errors['contact']?.['altLocation']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="city"
                  name="contact.altLocation.city"
                  label="City"
                  control={control}
                  errors={errors['contact']?.['altLocation']}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectField
                  id="state"
                  name="contact.altLocation.state"
                  label="State"
                  control={control}
                  errors={errors['contact']?.['altLocation']}
                  options={statesOptions}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="zip"
                  name="contact.altLocation.zip"
                  label="Zip"
                  control={control}
                  errors={errors['contact']?.['altLocation']}
                />
              </Grid>
            </Grid>
          </SubSection>
        </>
      )}
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <SubSection title="Claim Information">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <TextField
              id="claimNumber"
              name="claim.claimNumber"
              label="Claim Number"
              control={control}
              errors={errors['claim']}
              required
              inputProps={{ minLength: 5, maxLength: 40 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="jobId"
              name="claim.jobId"
              label="Job Id"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="taskId"
              name="claim.taskId"
              label="Task Id"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="insuredName"
              name="claim.insuredName"
              label="Insured Name"
              control={control}
              errors={errors['claim']}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="catCode"
              name="claim.catCode"
              label="catCode"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="propertyType"
              name="claim.propertyType"
              label="Property Type"
              control={control}
              errors={errors['claim']}
              options={PropertyTypeOptions}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimeField
              id="dateOfLoss"
              name="claim.dateOfLoss"
              label="Date of Loss"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimeField
              id="dateOfNotice"
              name="claim.dateOfNotice"
              label="Date of Notice"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="peril"
              name="claim.peril"
              label="Peril"
              control={control}
              errors={errors['claim']}
              options={perilOptions || []}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="sourcedFrom"
              name="claim.sourcedFrom"
              label="Sourced From"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBoxField
              id="claim.isModularHome"
              name="claim.isModularHome"
              label="Is Modular Home"
              control={control}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="jobNumber"
              name="claim.jobNumber"
              label="Job Number"
              control={control}
              errors={errors['claim']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <Section title="Contact Information">
        <SubSection title="Primary Contact">
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="name"
                name="contact.primary.name"
                label="Name"
                control={control}
                errors={errors['contact']?.['primary']}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="phone"
                name="contact.primary.phone"
                label="Phone"
                control={control}
                errors={errors['contact']?.['primary']}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="phone2"
                name="contact.primary.phone2"
                label="Alternate Phone"
                control={control}
                errors={errors['contact']?.['primary']}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="email"
                name="contact.primary.email"
                label="Email"
                control={control}
                errors={errors['contact']?.['primary']}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField
                id="contactMethod"
                name="contact.primary.contactMethod"
                label="Contact Method"
                control={control}
                errors={errors['contact']?.['primary']}
                options={IngressContactMethodOptions}
              />
            </Grid>
          </Grid>
        </SubSection>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <CheckBoxField
              id="contact.hasSecondaryContact"
              name="contact.hasSecondaryContact"
              label="Use Secondary Contact"
              control={control}
            />
          </Grid>
        </Grid>
        {watch('contact.hasSecondaryContact') && (
          <>
            <Divider style={{ marginTop: 20, marginBottom: 20 }} />
            <SubSection title="Secondary Contact">
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    id="name"
                    name="contact.secondary.name"
                    label="Name"
                    control={control}
                    errors={errors['contact']?.['secondary']}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="phone"
                    name="contact.secondary.phone"
                    label="Phone"
                    control={control}
                    errors={errors['contact']?.['secondary']}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="contact.secondary.phone2"
                    name="contact.secondary.phone2"
                    label="Alternate Phone"
                    control={control}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="email"
                    name="contact.secondary.email"
                    label="Email"
                    control={control}
                    errors={errors['contact']?.['secondary']}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SelectField
                    id="contactMethod"
                    name="contact.secondary.contactMethod"
                    label="Contact Method"
                    control={control}
                    errors={errors['contact']?.['secondary']}
                    options={IngressContactMethodOptions}
                  />
                </Grid>
              </Grid>
            </SubSection>
          </>
        )}
      </Section>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <SubSection title="Assignment Handling">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <CheckBoxField
              id="assignmentHandling.travel"
              name="assignmentHandling.travel"
              label="Travel Required"
              control={control}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="templateId"
              name="assignmentHandling.templateId"
              label="Template Id"
              control={control}
              errors={errors['assignmentHandling']}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="priority"
              name="assignmentHandling.priority"
              label="Priority"
              control={control}
              errors={errors['assignmentHandling']}
              options={PriorityLevelOptions}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="durationInMinutes"
              name="assignmentHandling.durationInMinutes"
              label="Duration (Minutes)"
              control={control}
              errors={errors['assignmentHandling']}
              type="number"
              required={isNonComplex}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="severity"
              name="assignmentHandling.severity"
              label="Severity"
              control={control}
              errors={errors['assignmentHandling']}
              options={SeverityOptions}
            />
          </Grid>
          {renderDynamicFields(isNonComplex)}
          <Grid item xs={3}>
            {featureFlags.qualifiedResourcesFnol ? (
              <TextField
                id="methodOfInspection"
                name="assignmentHandling.methodOfInspection"
                label="Method of Inspection"
                defaultValue={'FIELD'}
                control={control}
                errors={errors}
                disabled
              />
            ) : (
              <SelectField
                id="methodOfInspection"
                name="assignmentHandling.methodOfInspection"
                label="Method of Inspection"
                control={control}
                errors={errors}
                options={methodOfInspectionOptions || []}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="additionalComments"
              name="additionalData.additionalComments"
              label="Additional Comments"
              control={control}
              errors={errors['additionalData']}
              multiline
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="Referrers Phone"
              name="additionalData.referrersPhone"
              label="Referrers Phone"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="lineOfBusiness"
              name="additionalData.lineOfBusiness"
              label="Line of Business"
              control={control}
              errors={errors['additionalData']}
              options={[
                { title: 'APD', value: 'APD' },
                { title: 'Casualty', value: 'Casualty' },
                { title: 'Property', value: 'Property' },
              ]}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="strategicBusinessUnit"
              name="additionalData.strategicBusinessUnit"
              label="Strategic Business Unit"
              control={control}
              errors={errors['additionalData']}
              options={[
                { title: 'USRM', value: 'USRM' },
                { title: 'GRS', value: 'GRS' },
                { title: 'State Auto', value: 'State Auto' },
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="Exposure Number"
              name="additionalData.exposureNumber"
              label="Exposure Number"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="Scope of Work"
              name="additionalData.scopeOfWork"
              label="Scope of Work"
              control={control}
              errors={errors['additionalData']}
              multiline
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="Type of Report"
              name="additionalData.typeOfReport"
              label="Type of Report"
              control={control}
              errors={errors['additionalData']}
              multiline
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="Budget"
              name="additionalData.budget"
              label="Budget"
              control={control}
              errors={errors['additionalData']}
              multiline
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="fileInLitigation"
              name="additionalData.fileInLitigation"
              label="File In Litigation"
              control={control}
              errors={errors['additionalData']}
              options={[
                { title: 'Yes', value: 'yes' },
                { title: 'No', value: 'no' },
              ]}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20 }} />
      <SubSection title="Vehicle">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <TextField
              id="vehicleMake"
              name="additionalData.vehicleMake"
              label="Make"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="vehicleModel"
              name="additionalData.vehicleModel"
              label="Model"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="vehicleYear"
              name="additionalData.vehicleYear"
              label="Year"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="vehicleVinOrSerial"
              name="additionalData.vehicleVinOrSerial"
              label="Serial or VIN Number"
              control={control}
              errors={errors['additionalData']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <SubSection title="">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="notes"
              name="assignmentHandling.notes"
              label="Notes"
              control={control}
              errors={errors['assignmentHandling']}
              multiline
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="preferredLanguage"
              name="assignmentHandling.preferredLanguage"
              label="Preferred Language"
              control={control}
              errors={errors['assignmentHandling']}
              options={preferredLanguages || []}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="assignmentCategory"
              name="assignmentHandling.assignmentCategory"
              label="Assignment Category"
              control={control}
              errors={errors['assignmentHandling']}
              options={AssignmentCategoryOptions}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="assignmentType"
              name="assignmentHandling.assignmentType"
              label="Assignment Type"
              control={control}
              errors={errors['assignmentHandling']}
              options={AssignmentTypeOptions}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="inspectionType"
              name="assignmentHandling.inspectionType"
              label="Inspection Type"
              control={control}
              errors={errors['assignmentHandling']}
              options={InspectionTypeOptions}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <Section title="Other Information">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            {featureFlags.qualifiedResourcesFnol ? (
              <TextField
                id="fnolTaskType"
                name="assignmentHandling.taskType"
                label="Task Type"
                control={control}
                errors={errors}
                disabled
                defaultValue={'Claim Owner'}
                required
              />
            ) : (
              <SelectField
                id="fnolTaskType"
                name="assignmentHandling.taskType"
                label="Task Type"
                control={control}
                errors={errors}
                options={FNOL_TaskTypeOptions}
                required
              />
            )}
          </Grid>
        </Grid>
        <Divider style={{ marginTop: 20, marginBottom: 20 }} />
        {renderClaimAttributes()}
      </Section>
    </Section>
  );
};
